import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState("polling");
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="page-enter max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[#FACC15] mx-auto animate-spin mb-6" />
          <h1 className="font-heading font-black text-4xl tracking-tighter mb-3">{t("subscription_success.confirming")}</h1>
          <p className="text-white/50">{t("subscription_success.wait")}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#FACC15] mb-6">
            <Crown className="w-10 h-10 text-black" />
          </div>
          <CheckCircle2 className="w-6 h-6 text-[#00E5FF] mx-auto mb-3" />
          <h1 className="font-heading font-black text-5xl tracking-tighter mb-3">
            {t("subscription_success.welcome_1")} <span className="neon-yellow">{t("subscription_success.welcome_2")}</span>.
          </h1>
          <p className="text-white/60 max-w-md mx-auto mb-8 leading-relaxed">{t("subscription_success.welcome_desc")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/collection" data-testid="success-go-collection" className="btn-primary text-xs uppercase tracking-wider">{t("subscription_success.go_collection")}</Link>
            <Link to="/rankings" data-testid="success-go-rankings" className="btn-ghost text-xs uppercase tracking-wider">{t("subscription_success.explore_rankings")}</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle className="w-12 h-12 text-[#FF007F] mx-auto mb-6" />
          <h1 className="font-heading font-black text-4xl tracking-tighter mb-3">
            {status === "expired" ? t("subscription_success.expired_title") : t("subscription_success.failed_title")}
          </h1>
          <p className="text-white/50 mb-8">
            {status === "expired" ? t("subscription_success.expired_desc") : t("subscription_success.failed_desc")}
          </p>
          <Link to="/pricing" data-testid="failed-retry" className="btn-primary text-xs uppercase tracking-wider inline-flex">{t("subscription_success.back_pricing")}</Link>
        </>
      )}
      {info && status === "paid" && sessionId && (
        <p className="text-white/30 font-mono text-[10px] tracking-widest mt-12">// SESSION_ID · {sessionId.slice(0, 20)}…</p>
      )}
    </div>
  );
}
